import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Container from '../components/ui/Container';
import NotFoundPage from '../components/NotFoundPage';
import { slugifyTag } from '../utils/tags';
import './BlogTagsPage.css';

const BlogTagsPage = () => {
  const { tag: selectedTag } = useParams();
  const [posts, setPosts] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    
    fetch('/blog/index.json', { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load blog index');
        return res.json();
      })
      .then((data) => {
        if (!isMounted) return;
        
        // Extract all unique tags with post counts
        const tagCounts = {};
        data.forEach(post => {
          if (post.tags && Array.isArray(post.tags)) {
            post.tags.forEach(tag => {
              tagCounts[tag] = (tagCounts[tag] || 0) + 1;
            });
          }
        });
        
        const tagsWithCounts = Object.entries(tagCounts)
          .map(([tag, count]) => ({ tag, count }))
          .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
        
        setAllTags(tagsWithCounts);
        
        // Filter posts by selected tag if provided
        if (selectedTag) {
          // Compare slug-to-slug so tags that contain hyphens (e.g. "Real-time")
          // resolve correctly — never de-slugify the URL parameter.
          const selectedSlug = selectedTag.toLowerCase();

          const filteredPosts = data.filter(post =>
            post.tags && post.tags.some(tag => slugifyTag(tag) === selectedSlug)
          );
          setPosts(filteredPosts.sort((a, b) => new Date(b.date) - new Date(a.date)));
        } else {
          setPosts(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err.message || 'Error loading blog posts');
      })
      .finally(() => isMounted && setIsLoading(false));
    
    return () => {
      isMounted = false;
    };
  }, [selectedTag]);

  if (isLoading) {
    return (
      <div className="blog-tags-page page-with-header">
        <section className="tags-hero section">
          <Container>
            <div className="loading"><div className="spinner"></div></div>
          </Container>
        </section>
      </div>
    );
  }

  if (error) {
    return <NotFoundPage />;
  }

  // Check if selected tag exists
  const actualTag = selectedTag ? allTags.find(t =>
    slugifyTag(t.tag) === selectedTag.toLowerCase()
  )?.tag : null;
  
  if (selectedTag && !actualTag) {
    return <NotFoundPage />;
  }

  const pageTitle = actualTag 
    ? `${actualTag} Articles - PySolutions Blog`
    : 'Browse by Tags - PySolutions Blog';
    
  const pageDescription = actualTag
    ? `Articles tagged with "${actualTag}" from PySolutions. Practical insights on Python development, AI integration, and software engineering.`
    : 'Browse PySolutions blog articles by tags. Find content on Python, AI, FastAPI, performance, and software engineering topics.';

  const canonicalUrl = selectedTag
    ? `https://pysolutions.dev/blog/tags/${selectedTag}`
    : 'https://pysolutions.dev/blog/tags';

  return (
    <div className="blog-tags-page page-with-header">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content="https://pysolutions.dev/og-card.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="PySolutions" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://pysolutions.dev/og-card.jpg" />
      </Helmet>

      {/* Hero Section */}
      <section className="tags-hero section">
        <div className="tags-hero-background">
          <div className="hero-glow hero-glow-1"></div>
          <div className="hero-glow hero-glow-2"></div>
        </div>
        <Container>
          <div className="tags-hero-content">
            <div className="hero-badge">
              <span className="badge-icon">🏷️</span>
              {actualTag ? 'Tag Archive' : 'Browse by Tags'}
            </div>
            <h1 className="hero-title">
              {actualTag ? (
                <>Articles tagged <span className="logo-highlight">"{actualTag}"</span></>
              ) : (
                <>Explore <span className="logo-highlight">Content by Topic</span></>
              )}
            </h1>
            <p className="hero-description">
              {actualTag ? (
                `${posts.length} article${posts.length !== 1 ? 's' : ''} found`
              ) : (
                'Discover articles organized by technology, framework, and topic'
              )}
            </p>
            
            {actualTag && (
              <div className="hero-actions">
                <Link to="/blog/tags" className="btn-secondary">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Browse All Tags
                </Link>
                <Link to="/blog" className="btn-tertiary">
                  All Articles
                </Link>
              </div>
            )}
          </div>
        </Container>
      </section>

      {/* Tags Overview or Filtered Posts */}
      <section className="tags-content section">
        <Container>
          {!actualTag ? (
            /* All Tags View */
            <div className="tags-overview">
              <div className="section-header">
                <h2 className="section-title">Popular Tags</h2>
                <p className="section-subtitle">
                  Find articles by technology, framework, or topic
                </p>
              </div>
              
              <div className="tags-grid">
                {allTags.map(({ tag, count }) => (
                  <Link 
                    key={tag}
                    to={`/blog/tags/${slugifyTag(tag)}`}
                    className="tag-card glass-card"
                  >
                    <div className="tag-card-content">
                      <h3 className="tag-name">{tag}</h3>
                      <span className="tag-count">
                        {count} article{count !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="tag-card-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
              
              <div className="tags-actions">
                <Link to="/blog" className="btn-primary">
                  View All Articles
                  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                  </svg>
                </Link>
              </div>
            </div>
          ) : (
            /* Filtered Posts View */
            <div className="filtered-posts">
              {/* Related Tags */}
              <div className="related-tags">
                <h3 className="related-tags-title">Related Tags:</h3>
                <div className="related-tags-list">
                  {allTags
                    .filter(({ tag }) => tag !== actualTag)
                    .slice(0, 6)
                    .map(({ tag, count }) => (
                      <Link 
                        key={tag}
                        to={`/blog/tags/${slugifyTag(tag)}`}
                        className="related-tag-btn"
                      >
                        {tag} ({count})
                      </Link>
                    ))}
                </div>
              </div>

              {/* Posts Grid */}
              <div className="blog-grid">
                {posts.map((post) => (
                  <article key={post.slug} className="blog-card glass-card">
                    <Link to={`/blog/${post.slug}`} className="blog-card-link">
                      <div className="blog-card-media">
                        <img src={post.image || '/PySolutions.jpeg'} alt={post.title} loading="lazy" />
                      </div>
                      <div className="blog-card-content">
                        <div className="blog-card-meta">
                          <span className="badge">{new Date(post.date).toLocaleDateString()}</span>
                        </div>
                        <h2 className="blog-card-title">{post.title}</h2>
                        <p className="blog-card-desc">{post.description}</p>
                        <div className="blog-card-tags">
                          {post.tags && post.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="blog-card-tag">{tag}</span>
                          ))}
                        </div>
                        <div className="blog-card-footer">
                          <span className="read-more">Read more</span>
                          <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                          </svg>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>

              {posts.length === 0 && (
                <div className="no-posts">
                  <p>No articles found for this tag.</p>
                  <Link to="/blog/tags" className="btn-secondary">Browse All Tags</Link>
                </div>
              )}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
};

export default BlogTagsPage;
